import { type Language } from 'ecosystem'

export interface PostNordOverrides {
  noDeliveryOptions: string
  pickUpInStore: string
  pickUpInStoreDescription: string
  deliveryOption: string
  deliveryOptionDescription: string
  recipientTitle: string
  deliveryOptionsTitle: string
  pickupOptionsTitle: string
  pickUp: string
  days: string
  countrySweden: string
  countryNorway: string
  countryDenmark: string
  countryFinland: string
  fieldPostalCodeLabel: string
  fieldCountryCodeLabel: string
  fieldCityLabel: string
  fieldStreetNameLabel: string
  fieldStreetNumberLabel: string
  errorRequired: string
  errorInvalidFormat: string
  errorInvalidPostalCode: string
  noPickupLocationsAvailable: string
  contactStoreForPickupArrangements: string
  rememberOnDevice: string
}

export const postnordCheckoutTranslations: Record<Language, PostNordOverrides> = {
  en: {
    noDeliveryOptions: 'No delivery options available.',
    pickUpInStore: 'Pick up in store',
    pickUpInStoreDescription: 'You pick up in our store (shipping is canceled)',
    deliveryOption: 'Delivery',
    deliveryOptionDescription: 'We deliver to address of your choice',
    recipientTitle: 'Recipient details',
    deliveryOptionsTitle: 'Delivery options',
    pickupOptionsTitle: 'Select pickup location',

    pickUp: 'Pick up',
    days: 'days',

    countrySweden: 'Sweden',
    countryNorway: 'Norway',
    countryDenmark: 'Denmark',
    countryFinland: 'Finland',

    fieldPostalCodeLabel: 'Postal code',
    fieldCountryCodeLabel: 'Country',
    fieldCityLabel: 'City',
    fieldStreetNameLabel: 'Street name',
    fieldStreetNumberLabel: 'Street number',

    errorRequired: 'Required',
    errorInvalidFormat: 'Invalid format',
    errorInvalidPostalCode: 'Invalid postal code',
    noPickupLocationsAvailable: 'No pickup locations available',
    contactStoreForPickupArrangements: 'Contact the store for pickup arrangements',
    rememberOnDevice: 'Remember my details on this device'
  },
  no: {
    noDeliveryOptions: 'Ingen leveringsalternativer tilgjengelig.',
    pickUpInStore: 'Hent i butikken vår',
    pickUpInStoreDescription: 'Hent i butikken vår (frakt er kansellert)',
    deliveryOption: 'Levering',
    deliveryOptionDescription: 'Vi leverer til adressen du velger',
    recipientTitle: 'Mottakerdetaljer',
    deliveryOptionsTitle: 'Leveringsalternativer',
    pickupOptionsTitle: 'Velg hentested',

    pickUp: 'Hent',
    days: 'dager',

    countrySweden: 'Sverige',
    countryNorway: 'Norge',
    countryDenmark: 'Danmark',
    countryFinland: 'Finland',

    fieldPostalCodeLabel: 'Postnummer',
    fieldCountryCodeLabel: 'Land',
    fieldCityLabel: 'By',
    fieldStreetNameLabel: 'Gateadresse',
    fieldStreetNumberLabel: 'Husnummer',

    errorRequired: 'Påkrevd',
    errorInvalidFormat: 'Ugyldig format',
    errorInvalidPostalCode: 'Ugyldig postnummer',
    noPickupLocationsAvailable: 'Ingen hentesteder tilgjengelig',
    contactStoreForPickupArrangements: 'Kontakt butikken for hentearrangementer',
    rememberOnDevice: 'Husk opplysningene mine på denne enheten'
  },
  de: {
    noDeliveryOptions: 'Keine Lieferoptionen verfügbar.',
    pickUpInStore: 'Abholung im Laden',
    pickUpInStoreDescription: 'Abholung im Laden (Versand entfällt)',
    deliveryOption: 'Lieferung',
    deliveryOptionDescription: 'Wir liefern an die Adresse Ihrer Wahl',
    recipientTitle: 'Empfängerdetails',
    deliveryOptionsTitle: 'Lieferoptionen',
    pickupOptionsTitle: 'Abholort wählen',

    pickUp: 'Abholen',
    days: 'Tage',

    countrySweden: 'Schweden',
    countryNorway: 'Norwegen',
    countryDenmark: 'Dänemark',
    countryFinland: 'Finnland',

    fieldPostalCodeLabel: 'Postleitzahl',
    fieldCountryCodeLabel: 'Land',
    fieldCityLabel: 'Stadt',
    fieldStreetNameLabel: 'Straßenname',
    fieldStreetNumberLabel: 'Hausnummer',

    errorRequired: 'Erforderlich',
    errorInvalidFormat: 'Ungültiges Format',
    errorInvalidPostalCode: 'Ungültige Postleitzahl',
    noPickupLocationsAvailable: 'Keine Abholorte verfügbar',
    contactStoreForPickupArrangements: 'Wenden Sie sich für Abholvereinbarungen an das Geschäft',
    rememberOnDevice: 'Meine Daten auf diesem Gerät merken'
  },
  sv: {
    noDeliveryOptions: 'Inga leveransalternativ tillgängliga.',
    pickUpInStore: 'Hämta i vår butik',
    pickUpInStoreDescription: 'Hämta i vår butik (frakten stryks)',
    deliveryOption: 'Leverans',
    deliveryOptionDescription: 'Vi levererar till den adress du väljer',
    recipientTitle: 'Mottagaruppgifter',
    deliveryOptionsTitle: 'Leveransalternativ',
    pickupOptionsTitle: 'Välj upphämtningsställe',

    pickUp: 'Hämta',
    days: 'dagar',

    countrySweden: 'Sverige',
    countryNorway: 'Norge',
    countryDenmark: 'Danmark',
    countryFinland: 'Finland',

    fieldPostalCodeLabel: 'Postnummer',
    fieldCountryCodeLabel: 'Land',
    fieldCityLabel: 'Stad',
    fieldStreetNameLabel: 'Gatunamn',
    fieldStreetNumberLabel: 'Gatunummer',

    errorRequired: 'Obligatoriskt',
    errorInvalidFormat: 'Ogiltigt format',
    errorInvalidPostalCode: 'Ogiltigt postnummer',
    noPickupLocationsAvailable: 'Inga upphämtningsställen tillgängliga',
    contactStoreForPickupArrangements: 'Kontakta butiken för upphämtningsarrangemang',
    rememberOnDevice: 'Kom ihåg mina uppgifter på den här enheten'
  },
  da: {
    noDeliveryOptions: 'Ingen leveringsmuligheder tilgængelige.',
    pickUpInStore: 'Afhent i vores butik',
    pickUpInStoreDescription: 'Afhent i vores butik (fragt annulleres)',
    deliveryOption: 'Levering',
    deliveryOptionDescription: 'Vi leverer til den adresse, du vælger',
    recipientTitle: 'Modtageroplysninger',
    deliveryOptionsTitle: 'Leveringsmuligheder',
    pickupOptionsTitle: 'Vælg afhentningssted',

    pickUp: 'Afhentning',
    days: 'dage',

    countrySweden: 'Sverige',
    countryNorway: 'Norge',
    countryDenmark: 'Danmark',
    countryFinland: 'Finland',

    fieldPostalCodeLabel: 'Postnummer',
    fieldCountryCodeLabel: 'Land',
    fieldCityLabel: 'By',
    fieldStreetNameLabel: 'Gadenavn',
    fieldStreetNumberLabel: 'Gadenummer',

    errorRequired: 'Påkrævet',
    errorInvalidFormat: 'Ugyldigt format',
    errorInvalidPostalCode: 'Ugyldigt postnummer',
    noPickupLocationsAvailable: 'Ingen afhentningssteder tilgængelige',
    contactStoreForPickupArrangements: 'Kontakt butikken for afhentningsarrangementer',
    rememberOnDevice: 'Husk mine oplysninger på denne enhed'
  },
  fi: {
    noDeliveryOptions: 'Ei toimitusvaihtoehtoja saatavilla.',
    pickUpInStore: 'Nouda myymälästämme',
    pickUpInStoreDescription: 'Nouda myymälästämme (toimitus peruttu)',
    deliveryOption: 'Toimitus',
    deliveryOptionDescription: 'Toimitamme valitsemaasi osoitteeseen',
    recipientTitle: 'Vastaanottajan tiedot',
    deliveryOptionsTitle: 'Toimitusvaihtoehdot',
    pickupOptionsTitle: 'Valitse noutopaikka',

    pickUp: 'Nouda',
    days: 'päivää',

    countrySweden: 'Ruotsi',
    countryNorway: 'Norja',
    countryDenmark: 'Tanska',
    countryFinland: 'Suomi',

    fieldPostalCodeLabel: 'Postinumero',
    fieldCountryCodeLabel: 'Maa',
    fieldCityLabel: 'Kaupunki',
    fieldStreetNameLabel: 'Katuosoite',
    fieldStreetNumberLabel: 'Kadunnumero',

    errorRequired: 'Pakollinen',
    errorInvalidFormat: 'Virheellinen muoto',
    errorInvalidPostalCode: 'Virheellinen postinumero',
    noPickupLocationsAvailable: 'Noutopisteitä ei ole saatavilla',
    contactStoreForPickupArrangements: 'Ota yhteyttä myymälään noutojärjestelyistä',
    rememberOnDevice: 'Muista tietoni tällä laitteella'
  }
}
